import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowDownOnSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  FolderIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';

const ListsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedList, setSelectedList] = useState(null);
  const [newList, setNewList] = useState({
    name: '',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Get all users for admin filtering
  const { data: users } = useQuery(
    'users',
    async () => {
      // Only fetch if user is admin
      if (user.role === 'USER') return [];
      
      const response = await api.get('/api/users');
      return response.data;
    },
    {
      enabled: user.role !== 'USER'
    }
  );
  
  // Fetch lists with optional user filter
  const {
    data: lists,
    isLoading,
    isError
  } = useQuery(
    ['lists', selectedUserId],
    async () => {
      const params = new URLSearchParams();
      if (selectedUserId) params.append('userId', selectedUserId);
      
      const response = await api.get(`/api/lists${params.toString() ? `?${params.toString()}` : ''}`);
      return response.data;
    }
  );
  
  // Mutation for creating a new list
  const createListMutation = useMutation(
    (listData) => api.post('/api/lists', listData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('lists');
        setNewList({ name: '', description: '' });
        setIsCreating(false);
        setError(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to create list');
      }
    }
  );
  
  // Mutation for updating a list
  const updateListMutation = useMutation(
    ({ id, data }) => api.put(`/api/lists/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('lists');
        setSelectedList(null);
        setIsEditing(false);
        setError(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to update list');
      }
    }
  );
  
  // Mutation for deleting a list
  const deleteListMutation = useMutation(
    (id) => api.delete(`/api/lists/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('lists');
        setError(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to delete list');
      }
    }
  );
  
  // Mutation for setting a list as default
  const setDefaultListMutation = useMutation(
    (id) => api.put(`/api/lists/${id}/default`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('lists');
        setError(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to set default list');
      }
    }
  );
  
  // Mutation for marking a list as exported
  const markAsExportedMutation = useMutation(
    (id) => api.put(`/api/lists/${id}/export`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('lists');
        setError(null);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to mark list as exported');
      }
    }
  );
  
  // Handle form submission for creating a new list
  const handleCreateList = (e) => {
    e.preventDefault();
    setError(null);
    
    if (!newList.name.trim()) {
      setError('List name is required');
      return;
    }
    
    createListMutation.mutate(newList);
  };
  
  // Handle form submission for updating a list
  const handleUpdateList = (e) => {
    e.preventDefault();
    setError(null);
    
    if (!selectedList || !selectedList.name.trim()) {
      setError('List name is required');
      return;
    }
    
    updateListMutation.mutate({
      id: selectedList.id,
      data: {
        name: selectedList.name,
        description: selectedList.description,
        isActive: selectedList.isActive
      }
    });
  };
  
  // Handle deleting a list
  const handleDeleteList = (list) => {
    if (window.confirm(`Are you sure you want to delete the list "${list.name}"?`)) {
      deleteListMutation.mutate(list.id);
    }
  };
  
  // Handle setting a list as default
  const handleSetDefaultList = (list) => {
    setDefaultListMutation.mutate(list.id);
  };
  
  // Handle exporting a list
  const handleExportList = (list) => {
    // First, generate the export file (this would normally call the export API)
    alert(`Export list "${list.name}" to Excel`);
    
    // Then mark the list as exported
    markAsExportedMutation.mutate(list.id);
  };
  
  // Toggle list active status
  const toggleListActive = (list) => {
    updateListMutation.mutate({
      id: list.id,
      data: {
        ...list,
        isActive: !list.isActive
      }
    });
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="max-w-7xl mx-auto py-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Lists</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage your inventory lists for organizing items.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => setIsCreating(true)}
            className="btn-primary"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Create New List
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}
      
      {/* User filter for admins */}
      {(user.role === 'COMPANY_ADMIN' || user.role === 'SUPER_ADMIN') && (
        <div className="mt-4 sm:max-w-xs">
          <label htmlFor="userFilter" className="block text-sm font-medium text-gray-700">
            Filter by User
          </label>
          <select
            id="userFilter"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="form-input mt-1"
          >
            <option value="">All Users</option>
            {users?.map(user => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {/* Create list form */}
      {isCreating && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Create New List</h3>
            <form className="mt-5 space-y-4" onSubmit={handleCreateList}>
              <div>
                <label htmlFor="listName" className="block text-sm font-medium text-gray-700">
                  List Name
                </label>
                <input
                  type="text"
                  name="listName"
                  id="listName"
                  required
                  value={newList.name}
                  onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                  className="form-input mt-1"
                  placeholder="Enter list name"
                />
              </div>
              
              <div>
                <label htmlFor="listDescription" className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <textarea
                  name="listDescription"
                  id="listDescription"
                  rows={3}
                  value={newList.description}
                  onChange={(e) => setNewList({ ...newList, description: e.target.value })}
                  className="form-input mt-1"
                  placeholder="Enter description"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false);
                    setNewList({ name: '', description: '' });
                    setError(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createListMutation.isLoading}
                  className="btn-primary"
                >
                  {createListMutation.isLoading ? 'Creating...' : 'Create List'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit list form */}
      {isEditing && selectedList && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Edit List</h3>
            <form className="mt-5 space-y-4" onSubmit={handleUpdateList}>
              <div>
                <label htmlFor="editListName" className="block text-sm font-medium text-gray-700">
                  List Name
                </label>
                <input
                  type="text"
                  name="editListName"
                  id="editListName"
                  required
                  value={selectedList.name}
                  onChange={(e) => setSelectedList({ ...selectedList, name: e.target.value })}
                  className="form-input mt-1"
                  placeholder="Enter list name"
                />
              </div>
              
              <div>
                <label htmlFor="editListDescription" className="block text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <textarea
                  name="editListDescription"
                  id="editListDescription"
                  rows={3}
                  value={selectedList.description}
                  onChange={(e) => setSelectedList({ ...selectedList, description: e.target.value })}
                  className="form-input mt-1"
                  placeholder="Enter description"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="listIsActive"
                  checked={selectedList.isActive}
                  onChange={(e) => setSelectedList({ ...selectedList, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                />
                <label htmlFor="listIsActive" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedList(null);
                    setError(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateListMutation.isLoading}
                  className="btn-primary"
                >
                  {updateListMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Lists - Card Style */}
      <div className="mt-6 bg-white shadow rounded-lg">
        {/* Column Headers */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center space-x-6">
            <div className="text-sm font-medium text-gray-700">List Name</div>
            <div className="text-sm font-medium text-gray-700">Items</div>
            {(user.role === 'COMPANY_ADMIN' || user.role === 'SUPER_ADMIN') && (
              <div className="text-sm font-medium text-gray-700">Owner</div>
            )}
            <div className="text-sm font-medium text-gray-700">Status</div>
            <div className="text-sm font-medium text-gray-700">Last Exported</div>
          </div>
        </div>
        
        {/* Loading or No Items State */}
        {isLoading ? (
          <div className="py-10 text-center text-gray-500">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
            <p className="mt-2">Loading lists...</p>
          </div>
        ) : isError ? (
          <div className="py-10 text-center text-red-500">
            <p>Error loading lists. Please try again.</p>
          </div>
        ) : lists?.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No lists</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new list.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="btn-primary"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Create New List
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {lists.map((list) => (
              <div key={list.id} className={`py-3 ${list.defaultList ? 'bg-primary-50' : ''}`}>
                {/* List Row */}
                <div className="flex items-center justify-between py-2 px-4 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-4">
                    {/* List Name */}
                    <div className="min-w-[200px]">
                      <div className="flex items-center">
                        {list.defaultList && (
                          <span className="mr-2 inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                            Default
                          </span>
                        )}
                        <Link 
                          to={`/app/inventory?listId=${list.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600"
                        >
                          {list.name}
                        </Link>
                      </div>
                      {list.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">{list.description}</p>
                      )}
                    </div>
                    
                    {/* Item Count */}
                    <div className="text-sm text-gray-500 min-w-[60px]">
                      {list.itemCount}
                    </div>
                    
                    {/* Owner (Admin only) */}
                    {(user.role === 'COMPANY_ADMIN' || user.role === 'SUPER_ADMIN') && (
                      <div className="text-sm text-gray-500 min-w-[120px]">
                        <div className="flex items-center">
                          <UserIcon className="mr-1.5 h-4 w-4 text-gray-400" />
                          {list.owner}
                        </div>
                      </div>
                    )}
                    
                    {/* Status */}
                    <div className="min-w-[100px]">
                      {list.isActive ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          <CheckCircleIcon className="mr-1 h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                          <XCircleIcon className="mr-1 h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    {/* Last Exported */}
                    <div className="text-sm text-gray-500 min-w-[120px]">
                      {formatDate(list.exportedAt)}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-3">
                    {/* Edit button */}
                    <button
                      onClick={() => {
                        setSelectedList(list);
                        setIsEditing(true);
                      }}
                      className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    
                    {/* Export button */}
                    <button
                      onClick={() => handleExportList(list)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      disabled={list.itemCount === 0}
                    >
                      <ArrowDownOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    
                    {/* Set as default button (only for active lists) */}
                    {list.isActive && !list.defaultList && (
                      <button
                        onClick={() => handleSetDefaultList(list)}
                        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                      >
                        <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                    
                    {/* Toggle active status */}
                    {!list.defaultList && (
                      <button
                        onClick={() => toggleListActive(list)}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                      >
                        {list.isActive ? (
                          <XCircleIcon className="h-4 w-4 hover:text-gray-700" aria-hidden="true" />
                        ) : (
                          <CheckCircleIcon className="h-4 w-4 hover:text-green-600" aria-hidden="true" />
                        )}
                      </button>
                    )}
                    
                    {/* Delete button */}
                    {!list.defaultList && list.itemCount === 0 && (
                      <button
                        onClick={() => handleDeleteList(list)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListsPage;
